/**
 * Per-project UI preferences persisted in localStorage.
 *
 * These are deliberately NOT part of the OST payload synced to the server —
 * they are local, per-device settings (GitHub target, AI knobs, grid) that
 * happen to be scoped per project rather than globally.
 *
 * Keys are namespaced as `<key>_<projectId>`, an idiom that had been written
 * out by hand at every read and write site.
 */

/** Preference names this module manages. */
export type ProjectPrefKey =
    | 'githubRepo'
    | 'githubProjectId'
    | 'aiSuggestionCount'
    | 'aiModelQuality'
    | 'gridSpacing'
    | 'snapToGrid';

const scopedKey = (key: ProjectPrefKey, projectId: string): string => `${key}_${projectId}`;

/**
 * Writes a preference for the given project. A no-op when there is no active
 * project — a preference set before one is open has nowhere to belong.
 */
export function writeProjectPref(projectId: string | null | undefined, key: ProjectPrefKey, value: string): void {
    if (!projectId) return;
    localStorage.setItem(scopedKey(key, projectId), value);
}

/** Reads a raw preference string for the given project, or null when unset. */
export function readProjectPref(projectId: string, key: ProjectPrefKey): string | null {
    return localStorage.getItem(scopedKey(key, projectId));
}

/**
 * Deletes a GitHub PAT left in localStorage by an older build.
 *
 * The privacy policy says the token is held in memory only, and
 * `setGithubToken` honours that — but a build predating that decision wrote it
 * under the unscoped `githubToken` key, where it would otherwise sit
 * indefinitely. Called once at startup.
 *
 * Safe to call repeatedly; removing an absent key is a no-op.
 */
export function purgeLegacyGithubToken(): void {
    try {
        localStorage.removeItem('githubToken');
    } catch {
        // Storage can throw in private-mode / disabled-storage browsers. A failed
        // purge must never prevent the app from starting.
    }
}
